import { useEffect, useMemo, useRef, useState } from "react";
import { getSessionStorageItem } from "presentation/utils/sessionStorage";
import {
  DataTable,
  DataTableFilterEvent,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import Spinner from "presentation/components/Shared/Spinner";
import {
  translateRegexMessage,
  translateText,
} from "infra/translation/translate";

import { useFilterBlogStore } from "presentation/hooks/use-filter-blog-store";

import { useQueryClient } from "@tanstack/react-query";
import { httpClient } from "infra/axios/axios-api";
import { Dialog } from "primereact/dialog";
import { ProgressBar } from "primereact/progressbar";
import { Page, PAGE_TYPES } from "domain/entities/Page";
import {
  useCreatePage,
  useDeletePage,
  usePages,
  useRenamePage,
} from "presentation/hooks/use-query-website";

const toSlug = (name: string) => {
  return (name ?? "").trim().toLowerCase().replace(/\s+/g, "-");
};

type SortOrder = "asc" | "desc" | "";

function base64url(str: string) {
  const b64 = btoa(unescape(encodeURIComponent(str)));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const WebsiteBlog: React.FC = () => {
  const toast = useRef<Toast>(null);

  const { filters, setFilters } = useFilterBlogStore();

  const [lazyParams, setLazyParams] = useState({
    sortBy: "name",
    sortOrder: "asc" as SortOrder,
    limit: 50,
    page: 1,
    filters: {} as Record<string, unknown>,
  });

  const {
    data: Pages,
    isLoading,
    isError,
    error,
    refetch,
  } = usePages(PAGE_TYPES.blog);

  const createMut = useCreatePage();
  const renameMut = useRenamePage();
  const deleteMut = useDeletePage();

  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  type StepStatus = "pending" | "done" | "error";
  type Step = { key: string; label: string; status: StepStatus };

  const [isCreating, setIsCreating] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);

  const EDIT_ORIGIN =
    window.location.hostname === "localhost"
      ? "http://localhost:5173"
      : "https://www.nimber.pt";

  const initSteps = (blogName: string) => {
    setSteps([
      {
        key: "createPage",
        label: `Criando blog "${blogName}"`,
        status: "pending",
      },
      {
        key: "createSections",
        label: "Criando seções (S1)",
        status: "pending",
      },
      {
        key: "createS1Components",
        label: "Criando componentes do S1",
        status: "pending",
      },
      { key: "finalizing", label: "Finalizando", status: "pending" },
    ]);
  };

  const setStepStatus = (key: string, status: StepStatus) =>
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, status } : s)));

  const progress = steps.length
    ? Math.round(
        (steps.filter((s) => s.status === "done").length / steps.length) * 100
      )
    : 0;

  const doStep = async (key: string, fn: () => Promise<void>) => {
    try {
      await fn();
      setStepStatus(key, "done");
    } catch (e) {
      setStepStatus(key, "error");
      throw e;
    }
  };

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if (
              typeof value === "object" &&
              value !== null &&
              "constraints" in value
            ) {
              return [
                key,
                Array.isArray(value.constraints)
                  ? value.constraints[0]?.value ?? ""
                  : "",
              ];
            }
            return [key, value?.value ?? ""];
          })
          .filter(([, v]) => v !== null && v !== undefined && v !== "")
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [lazyParams.filters, refetch]);

  useEffect(() => {
    if (isError && toast.current) {
      toast.current.show({
        severity: "error",
        summary: "Erro",
        detail:
          translateRegexMessage((error as Error)?.message, "pt") ||
          "Ocorreu um erro inesperado",
        life: 2000,
      });
    }
  }, [isError, error]);

  const listFiltered = useMemo(() => {
    const list = Pages ?? [];
    const nameFilter = String(lazyParams.filters?.["name"] ?? "").toLowerCase();
    if (!nameFilter) return list;
    return list.filter((p: any) =>
      (p.name ?? "").toLowerCase().includes(nameFilter)
    );
  }, [Pages, lazyParams.filters]);

  const listSorted = useMemo(() => {
    const { sortBy, sortOrder } = lazyParams;
    if (!sortBy || !sortOrder) return listFiltered;

    const arr = [...listFiltered];
    arr.sort((a, b) => {
      const av = (a as any)?.[sortBy];
      const bv = (b as any)?.[sortBy];

      if (sortBy === "createdAt" || sortBy === "modifiedAt") {
        const at = av ? new Date(av).getTime() : 0;
        const bt = bv ? new Date(bv).getTime() : 0;
        return sortOrder === "asc" ? at - bt : bt - at;
      }

      const as = String(av ?? "").toLowerCase();
      const bs = String(bv ?? "").toLowerCase();
      if (as < bs) return sortOrder === "asc" ? -1 : 1;
      if (as > bs) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return arr;
  }, [listFiltered, lazyParams]);

  const totalRecords = listSorted.length;

  const pageData = useMemo(() => {
    const start = (lazyParams.page - 1) * lazyParams.limit;
    return listSorted.slice(start, start + lazyParams.limit);
  }, [listSorted, lazyParams.page, lazyParams.limit]);

  const askConfirm = (message: string, header = "Confirmar exclusão") =>
    new Promise<boolean>((resolve) => {
      confirmDialog({
        message,
        header,
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Excluir",
        rejectLabel: "Cancelar",
        acceptClassName: "p-button-danger",
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });

  const onDelete = async (row: Page) => {
    const ok = await askConfirm(
      `Excluir "${row.name}"? Esta ação não pode ser desfeita.`
    );
    if (!ok) return;

    deleteMut.mutate(
      { id: row.id },
      {
        onSuccess: () =>
          toast.current?.show({
            severity: "success",
            summary: "Excluído",
            detail: `"${row.name}" foi removido.`,
            life: 1500,
          }),
        onError: (e: any) =>
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: e?.message ?? "Falha ao excluir.",
            life: 1500,
          }),
      }
    );
  };

  const qc = useQueryClient();

  const createSection = async (pageId: string, name: string, cols = 0) => {
    const { data } = await httpClient.post(`/section`, {
      data: { pageId, name, cols },
    });
    return data?.data;
  };

  const createComponent = async (input: {
    sectionId: string;
    name: string;
    type: "text" | "image";
    value: string | Record<string, unknown>;
    order: number;
    position?: string;
  }) => {
    const { data } = await httpClient.post(
      `/component`,
      { data: input },
      { timeout: 20000 }
    );
    return data?.data;
  };

  const setStepLabel = (key: string, label: string) =>
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, label } : s)));

  async function createInBatches(
    sectionId: string,
    defs: Array<{
      name: string;
      type: "text" | "image";
      value: any;
      order: number;
      position: string;
    }>,
    stepKey: string,
    batchSize = 3,
    pauseMs = 120
  ) {
    const total = defs.length;
    let done = 0;

    for (let i = 0; i < defs.length; i += batchSize) {
      const batch = defs.slice(i, i + batchSize);

      await Promise.all(batch.map((d) => createComponent({ sectionId, ...d })));

      done += batch.length;
      setStepLabel(
        stepKey,
        `${
          steps.find((s) => s.key === stepKey)?.label.split(" (")[0]
        } (${done}/${total})`
      );

      if (pauseMs > 0 && i + batchSize < defs.length) {
        await new Promise((r) => setTimeout(r, pauseMs));
      }
    }
  }

  // CREATE com defaults (Blog)
  const onCreate = async () => {
    const n = newName.trim();
    if (!n) {
      toast.current?.show({
        severity: "warn",
        summary: "Nome obrigatório",
        detail: "Informe um nome para o blog.",
        life: 1500,
      });
      return;
    }

    setIsCreating(true);
    initSteps(n);

    createMut.mutate(
      { name: n, type: PAGE_TYPES.blog },
      {
        onSuccess: async (res: any) => {
          try {
            setStepStatus("createPage", "done");

            const pageCreated = res?.data ?? res;
            const pageId: string = pageCreated?.id;
            if (!pageId) throw new Error("ID da página não retornado.");

            await doStep("createSections", async () => {
              const sectionS1 = await createSection(pageId, `${n}_S1`, 0);

              const s1Id: string = sectionS1?.id;
              if (!s1Id) {
                throw new Error(
                  "Falha ao criar uma ou mais sections (Header/S1/S2)."
                );
              }
              (onCreate as any)._ids = { s1Id };
            });

            const { s1Id } = (onCreate as any)._ids as {
              s1Id: string;
            };

            // Defaults S1 (Blog content)
            const s1Defaults = [
              {
                name: `${n}_S1_TXT01`,
                type: "text" as const,
                value: `${n}`,
                order: 1,
                position: "",
              },
              {
                name: `${n}_S1_TXT02`,
                type: "text" as const,
                value: `IT consultancy`,
                order: 2,
                position: "",
              },
              {
                name: `${n}_S1_TXT03`,
                type: "text" as const,
                value: `Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry.`,
                order: 3,
                position: "",
              },
              {
                name: `${n}_S1_TXT04`,
                type: "text" as const,
                value: `Sabias que se sugerires um amigo à Nimber podes ganhar até 1000€?`,
                order: 4,
                position: "",
              },
              {
                name: `${n}_S1_TXT05`,
                type: "text" as const,
                value: `Sugere agora!`,
                order: 5,
                position: "",
              },
              {
                name: `${n}_S1_IMG01`,
                type: "image" as const,
                value: `/Blog/blog-background.png`,
                order: 1,
                position: "",
              },
            ];

            await doStep("createS1Components", async () => {
              await createInBatches(s1Id, s1Defaults, "createS1Components", 3);
            });

            await doStep("finalizing", async () => {
              setNewName("");
              await qc.invalidateQueries({ queryKey: ["blog-pages"] });
            });

            toast.current?.show({
              severity: "success",
              summary: "Criado",
              detail: `Blog "${n}" criado com Header, S1 e S2 padrão.`,
              life: 1800,
            });
          } catch (e: any) {
            toast.current?.show({
              severity: "error",
              summary: "Erro ao criar defaults",
              detail: e?.message ?? "Falha ao criar seção/componentes padrão.",
              life: 2500,
            });
          } finally {
            setIsCreating(false);
          }
        },
        onError: (e: any) => {
          setStepStatus("createPage", "error");
          setIsCreating(false);
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: e?.message ?? "Falha ao criar a página.",
            life: 2000,
          });
        },
      }
    );
  };

  const onSaveRename = (row: Page) => {
    const nn = editingName.trim();
    if (!nn) {
      toast.current?.show({
        severity: "warn",
        summary: "Nome obrigatório",
        detail: "Informe um novo nome.",
        life: 1500,
      });
      return;
    }
    renameMut.mutate(
      { id: row.id, name: nn },
      {
        onSuccess: () => {
          setEditingId(null);
          toast.current?.show({
            severity: "success",
            summary: "Atualizado",
            detail: `Nome alterado para "${nn}".`,
            life: 1500,
          });
        },
        onError: (e: any) =>
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: e?.message ?? "Falha ao salvar.",
            life: 1500,
          }),
      }
    );
  };

  const goEdit = (row: Page) => {
    const slug = toSlug(row.name ?? "");
    const raw = getSessionStorageItem("auth-session-storage");
    if (!raw) {
      alert("Sem sessão válida para editar.");
      return;
    }
    const parsed = JSON.parse(raw);
    const st = parsed?.state ?? {};
    if (!st.accessToken) {
      alert("Sem token para editar.");
      return;
    }
    const payload = {
      accessToken: st.accessToken,
      refreshToken: st.refreshToken,
      expiresAt: st.expiresAt,
      userPermissions: st.userPermissions,
    };
    const tok = base64url(JSON.stringify(payload));

    const url = `${EDIT_ORIGIN}/edit/blog/${encodeURIComponent(
      slug
    )}#tok=${tok}`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (isLoading) return <Spinner />;

  return (
    <div>
      <div className="flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">{translateText("Blog", "pt")}</h2>

        <div className="flex gap-2">
          <span className="p-input-icon-left">
            <input
              className="p-inputtext p-component"
              style={{ minWidth: 280 }}
              placeholder={translateText("New blog", "pt")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onCreate();
              }}
            />
          </span>
          <button
            className="p-button p-component p-button-sm"
            onClick={onCreate}
            disabled={createMut.isPending || isCreating}
          >
            {createMut.isPending || isCreating ? (
              translateText("Saving…", "pt")
            ) : (
              <i className="pi pi-plus" />
            )}
          </button>
        </div>
      </div>

      <DataTable
        value={pageData}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={totalRecords}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: String(e.sortField ?? "name"),
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        rowsPerPageOptions={[10, 25, 50]}
        dataKey="id"
        header={null}
      >
        <Column
          field="name"
          header={translateText("Name", "pt")}
          sortable
          filter
          showFilterMatchModes={false}
          body={(row: Page) =>
            editingId === row.id ? (
              <input
                className="p-inputtext p-component w-full"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
              />
            ) : (
              row.name
            )
          }
        />

        <Column
          header={translateText("Slug", "pt")}
          body={(row: Page) => toSlug(row.name ?? "")}
          style={{ width: "18rem" }}
        />

        <Column
          field="createdAt"
          header={translateText("Created at", "pt")}
          sortable
          body={(row: Page) =>
            row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"
          }
          style={{ width: "14rem" }}
        />

        <Column
          field="modifiedAt"
          header={translateText("Updated at", "pt")}
          sortable
          body={(row: Page) =>
            row.modifiedAt ? new Date(row.modifiedAt).toLocaleString() : "-"
          }
          style={{ width: "14rem" }}
        />

        <Column
          header={translateText("Actions", "pt")}
          align="center"
          alignHeader="center"
          body={(row: Page) => (
            <div className="flex gap-2 justify-content-center">
              {editingId === row.id ? (
                <>
                  <button
                    className="p-button p-component p-button-sm"
                    onClick={() => onSaveRename(row)}
                    disabled={renameMut.isPending}
                  >
                    {renameMut.isPending
                      ? translateText("Saving…", "pt")
                      : translateText("Save", "pt")}
                  </button>
                  <button
                    className="p-button p-button-text p-component p-button-sm"
                    onClick={() => setEditingId(null)}
                  >
                    {translateText("Cancel", "pt")}
                  </button>
                </>
              ) : (
                <>
                  <i
                    className="pi pi-pencil cursor-pointer"
                    title={translateText("Rename", "pt")}
                    onClick={() => {
                      setEditingId(row.id);
                      setEditingName(row.name ?? "");
                    }}
                  />
                  <i
                    className="pi pi-file-edit cursor-pointer"
                    title={translateText("Edit content", "pt")}
                    onClick={() => goEdit(row)}
                  />
                  <i
                    className="pi pi-trash cursor-pointer"
                    title={translateText("Delete", "pt")}
                    onClick={() => onDelete(row)}
                  />
                </>
              )}
            </div>
          )}
          style={{ width: "14rem" }}
        />
      </DataTable>

      <Toast ref={toast} position="bottom-right" />
      <ConfirmDialog />
      <Dialog
        visible={isCreating}
        header={translateText("Creating blog", "pt")}
        modal
        closable={false}
        style={{ width: "32rem", maxWidth: "90vw" }}
        onHide={() => {}}
      >
        <div className="flex align-items-center gap-2 mb-3">
          <i className="pi pi-cog pi-spin " />
          <span className="text-500">
            {translateText("Please wait…", "pt")}
          </span>
        </div>

        <ul className="p-0 m-0" style={{ listStyle: "none" }}>
          {steps.map((s) => (
            <li key={s.key} className="flex align-items-center gap-2 mb-2">
              <i
                className={
                  s.status === "done"
                    ? "pi pi-check-circle text-green-500"
                    : s.status === "error"
                    ? "pi pi-times-circle text-red-500"
                    : "pi pi-circle-on text-500"
                }
              />
              <span>{s.label}</span>
            </li>
          ))}
        </ul>

        <div className="mt-3">
          <ProgressBar value={progress} />
        </div>
      </Dialog>
    </div>
  );
};

export default WebsiteBlog;
